import React, { useRef } from "react";
import { clsx } from "clsx";
import { useItemUpload } from "../hooks/useItemUpload";
import { Button, Text } from "../../../ui";
import type { UploadItemFile } from "../types";

export interface ItemUploaderProps {
  /**
   * Callback when upload is successfully completed
   */
  onUploadComplete?: (item: UploadItemFile) => void;

  /**
   * Callback when upload is cancelled
   */
  onUploadCancel?: () => void;

  /**
   * Initial owner value (optional)
   */
  initialOwner?: string;

  /**
   * Custom CSS classes
   */
  className?: string;

  /**
   * Show owner field (default: true)
   */
  showOwner?: boolean;

  /**
   * Accept file types (default: all)
   */
  accept?: string;

  /**
   * Maximum file size in bytes (default: 100MB)
   */
  maxFileSize?: number;
}

export const ItemUploadComponent = ({
  onUploadComplete,
  onUploadCancel,
  initialOwner = "",
  className = "",
  showOwner = true,
  accept,
  maxFileSize = 10000 * 1024 * 1024, // 100MB
}: ItemUploaderProps) => {
  const upload = useItemUpload();
  const hiddenFileInput = useRef<HTMLInputElement>(null);

  // Initialize owner if provided
  React.useEffect(() => {
    if (initialOwner && upload.state.currentItem?.owner !== initialOwner) {
      upload.actions.setOwner(initialOwner);
    }
  }, [initialOwner, upload.actions, upload.state.currentItem?.owner]);

  // Handle upload completion callback
  React.useEffect(() => {
    if (
      !upload.state.isUploading &&
      upload.state.progress?.percentage === 100 &&
      upload.state.currentItem
    ) {
      onUploadComplete?.(upload.state.currentItem);
    }
  }, [
    upload.state.isUploading,
    upload.state.progress?.percentage,
    upload.state.currentItem,
    onUploadComplete,
  ]);

  const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file) {
      // Check file size
      if (file.size > maxFileSize) {
        // File size validation will be handled by the service
        console.warn(
          `File size exceeds ${Math.round(maxFileSize / (1024 * 1024))}MB limit`
        );
        return;
      }
      upload.actions.setFile(file);
    }
  };

  const handleBrowseClick = () => {
    if (!upload.state.isUploading) {
      hiddenFileInput.current?.click();
    }
  };

  const handleUpload = async () => {
    await upload.actions.startUpload();
  };

  const handleCancel = () => {
    upload.actions.cancelUpload();
    onUploadCancel?.();
  };

  const handleNameChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    upload.actions.setName(event.target.value);
  };

  const handleOwnerChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    upload.actions.setOwner(event.target.value);
  };

  const currentItem = upload.state.currentItem;
  const isDisabled = upload.computed.isDisabled;

  return (
    <div className={clsx("space-y-4", className)}>
      <Text variant="title" align="left">
        Upload File
      </Text>

      {/* Error Display */}
      {upload.state.error && (
        <div className="p-3 rounded-md bg-red-50 border border-red-200 text-red-700 text-sm">
          {upload.state.error}
          <button
            onClick={upload.actions.clearError}
            className="ml-2 text-red-500 hover:text-red-700 font-medium"
          >
            ×
          </button>
        </div>
      )}

      {/* File Selection */}
      <div>
        <label className="block text-sm font-medium text-text-primary mb-2">
          File
        </label>
        <div className="flex gap-2 overflow-hidden">
          <div
            className={clsx(
              "flex-1 border border-border-primary min-w-0 rounded-md p-3 bg-bg-secondary text-text-primary transition-colors cursor-pointer",
              upload.state.isUploading
                ? "opacity-50 cursor-not-allowed"
                : "hover:bg-bg-tertiary",
              upload.state.error && "border-red-300"
            )}
            onClick={handleBrowseClick}
          >
            {currentItem?.file ? (
              <div className="flex items-center justify-between">
                <span className="truncate">{currentItem.file.name}</span>
                <span className="text-xs text-text-secondary ml-2">
                  {Math.round(currentItem.file.size / 1024)} KB
                </span>
              </div>
            ) : (
              <span className="text-text-secondary">No file selected</span>
            )}
          </div>
          <input
            type="file"
            onChange={handleFileChange}
            ref={hiddenFileInput}
            className="hidden"
            disabled={upload.state.isUploading}
            accept={accept}
          />
          <Button
            onClick={handleBrowseClick}
            disabled={upload.state.isUploading}
            variant="secondary"
          >
            Browse
          </Button>
        </div>
      </div>

      {/* Name Input */}
      <div>
        <label className="block text-sm font-medium text-text-primary mb-2">
          Name
        </label>
        <input
          type="text"
          placeholder="Enter file name"
          value={currentItem?.name || ""}
          onChange={handleNameChange}
          disabled={upload.state.isUploading}
          className={clsx(
            "w-full border border-border-primary rounded-md p-3 bg-bg-primary text-text-primary placeholder-text-muted focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent transition-colors",
            upload.state.isUploading && "opacity-50 cursor-not-allowed",
            upload.state.error && "border-red-300"
          )}
        />
      </div>

      {/* Owner Input (Optional) */}
      {showOwner && (
        <div>
          <label className="block text-sm font-medium text-text-primary mb-2">
            Owner (optional)
          </label>
          <input
            type="text"
            placeholder="Enter file owner"
            value={currentItem?.owner || ""}
            onChange={handleOwnerChange}
            disabled={upload.state.isUploading}
            className={clsx(
              "w-full border border-border-primary rounded-md p-3 bg-bg-primary text-text-primary placeholder-text-muted focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent transition-colors",
              upload.state.isUploading && "opacity-50 cursor-not-allowed"
            )}
          />
        </div>
      )}

      {/* Progress Bar */}
      {upload.state.isUploading && upload.state.progress && (
        <div>
          <div className="flex justify-between items-center text-sm text-text-secondary mb-2">
            <span>Uploading...</span>
            <span className="font-medium">
              {upload.state.progress.percentage}%
            </span>
          </div>
          <div className="w-full bg-bg-tertiary rounded-full h-2 overflow-hidden">
            <div
              className="bg-primary-500 h-full rounded-full transition-all duration-300 ease-out"
              style={{ width: `${upload.state.progress.percentage}%` }}
            />
          </div>
          <div className="flex justify-between items-center mt-2 text-xs text-text-muted">
            <span>
              {Math.round(upload.state.progress.loaded / 1024)} KB /{" "}
              {Math.round(upload.state.progress.total / 1024)} KB
            </span>
          </div>
        </div>
      )}

      {/* Action Buttons */}
      <div className="flex justify-end gap-2">
        {upload.state.isUploading ? (
          <Button onClick={handleCancel} variant="secondary">
            Cancel Upload
          </Button>
        ) : (
          <>
            {upload.computed.hasFile && (
              <Button onClick={upload.actions.resetUpload} variant="secondary">
                Clear
              </Button>
            )}
            <Button onClick={handleUpload} disabled={isDisabled}>
              Upload
            </Button>
          </>
        )}
      </div>
    </div>
  );
};
